import type { CalibrationData } from "./linearizationAlgorithm";
import {
  VISCOSITY_PRESSURE_TYPE,
  VISCOSITY_TEMPERATURE_TYPE
} from "@/libs/calculations/constants";
// This file is from Imperial Fluid Properties - with calcs 2.xlsx
// Our grouping algo is different since they are arbitrary groups
// in the spread sheet. We try to get at least 3 data points for each
// group for interpolation.

// Data grouping for Lagrange interpolation
export type LagrangeDataGrouping = {
  average: number;
  points: Array<{ x: number; y: number }>;
};

// Enhanced interpolation data with groupings
export type LagrangeInterpolationDataWithGroupings = {
  type: string;
  dataGroupings: LagrangeDataGrouping[];
};

// Map for all fluid properties with groupings
export type LagrangeInterpolationMapWithGroupings = {
  bubblePointPressurePSIA: LagrangeInterpolationDataWithGroupings | null;
  oilFormationVolumeFactorBBLSTB: LagrangeInterpolationDataWithGroupings | null;
  solutionGasOilRatioSCFSTB: LagrangeInterpolationDataWithGroupings | null;
  waterFormationVolumeFactorBBLSTB: LagrangeInterpolationDataWithGroupings | null;
  solutionGasWaterRatioSCFSTB: LagrangeInterpolationDataWithGroupings | null;
  gasFormationVolumeFactorRCFSCF: LagrangeInterpolationDataWithGroupings | null;
  liveOilViscosityCP: LagrangeInterpolationDataWithGroupings | null;
  waterViscosityCP: LagrangeInterpolationDataWithGroupings | null;
  gasViscosityCP: LagrangeInterpolationDataWithGroupings | null;
  deadOilViscosityCP: LagrangeInterpolationDataWithGroupings | null;
};

/**
 * Build Lagrange data structure with groupings for interpolation
 * @param dataRows Array of calibration data
 * @returns Lagrange interpolation map with groupings
 */
export function buildLagrangeDataStructure(
  dataRows: CalibrationData[]
): LagrangeInterpolationMapWithGroupings {
  // This function creates data groupings from calibration data:
  // Creates a structure with all fluid properties organized by groupings
  // Handles edge cases (empty data, insufficient points)

  const properties: Array<keyof LagrangeInterpolationMapWithGroupings> = [
    "bubblePointPressurePSIA",
    "oilFormationVolumeFactorBBLSTB",
    "solutionGasOilRatioSCFSTB",
    "waterFormationVolumeFactorBBLSTB",
    "solutionGasWaterRatioSCFSTB",
    "gasFormationVolumeFactorRCFSCF",
    "liveOilViscosityCP",
    "waterViscosityCP",
    "gasViscosityCP",
    "deadOilViscosityCP"
  ];

  const result: Partial<LagrangeInterpolationMapWithGroupings> = {};
  const dataSetType = dataRows[0]?.controlType || VISCOSITY_PRESSURE_TYPE;

  properties.forEach(prop => {
    // Collect all valid data points for this property
    const allDataPoints: Array<{ x: number; y: number }> = [];

    dataRows.forEach(point => {
      const xVal = point.controllingValue;
      const yVal = point[prop as keyof CalibrationData] as number | null;

      if (
        typeof xVal === "number" &&
        !isNaN(xVal) &&
        xVal !== 0 &&
        typeof yVal === "number" &&
        !isNaN(yVal) &&
        yVal !== 0
      ) {
        allDataPoints.push({ x: xVal, y: yVal });
      }
    });

    if (allDataPoints.length === 0) {
      result[prop] = null;
    } else if (allDataPoints.length === 1) {
      result[prop] = {
        type: dataSetType,
        dataGroupings: [{ average: allDataPoints[0].x, points: allDataPoints }]
      };
    } else {
      // Sort points by x value
      allDataPoints.sort((a, b) => a.x - b.x);

      const dataGroupings: LagrangeDataGrouping[] = [];
      const grouped = [];
      let i = 0;
      // Try to group points by at least 3 each, with a minimum of 2
      while (i < allDataPoints.length) {
        // Calculate the remaining number of objects
        const remaining = allDataPoints.length - i;

        // If the remaining objects are fewer than the minimum group size (2),
        // merge them into the previous group to ensure all objects are included
        if (remaining < 2) {
          grouped[grouped.length - 1].push(...allDataPoints.slice(i));
          break;
        }

        // Create a group with a size between 2 and 3
        const groupSize = Math.min(3, Math.max(2, remaining));
        grouped.push(allDataPoints.slice(i, i + groupSize));
        i += groupSize;
      }

      // Special case since we try to group 3 at a time
      // If there's only 4 rows, split into 2 and 2
      if (grouped.length === 1 && grouped[0].length === 4) {
        grouped.push(grouped[0].splice(2, 2));
      }

      // Find averages of all the groups
      for (let j = 0; j < grouped.length; j++) {
        const average =
          grouped[j].reduce((sum, p) => sum + p.x, 0) / grouped[j].length;
        dataGroupings.push({ average, points: grouped[j] });
      }
      result[prop] = {
        type: dataSetType,
        dataGroupings
      };
    }
  });

  return result as LagrangeInterpolationMapWithGroupings;
}

/**
 * Perform Lagrange polynomial interpolation using 3 points
 * Matches the spreadsheet formula exactly
 * @param dataPoints Array of {x, y} points
 * @param x The x value to interpolate at
 * @returns The interpolated y value
 */
function lagrangeInterpolate(
  dataPoints: Array<{ x: number; y: number }>,
  controlValue: number
): number {
  if (dataPoints.length === 0) return 0;
  if (dataPoints.length === 1) return dataPoints[0].y;

  const sortedPoints = [...dataPoints].sort((a, b) => a.x - b.x);
  let result = 0;

  // Actual lagrange algo here
  for (let i = 0; i < sortedPoints.length; i++) {
    let term = sortedPoints[i].y;
    for (let j = 0; j < sortedPoints.length; j++) {
      if (j !== i) {
        // Same math as sheet but looks a little different
        // Sheet has term * (((x1-y2)*(x1-y3)*...)/((y1-x2)*(y1-x3)*...))
        // We have term * ((x1-y2)/(y1-x2)) * ((x1-y3)/(y1-x3)) * ...
        term *=
          (controlValue - sortedPoints[j].x) /
          (sortedPoints[i].x - sortedPoints[j].x);
      }
    }
    result += term;
  }

  return result;
}

/**
 * Evaluate using Lagrange interpolation with groupings
 * @param propertyData Property data with groupings
 * @param pressure Pressure value
 * @param temperature Temperature value
 * @returns Interpolated value
 */
export function evaluateLagrangePolynomialWithGroupings(
  propertyData: LagrangeInterpolationDataWithGroupings | null,
  pressure: number,
  temperature: number
): number {
  // Selects the appropriate data grouping based on which average is closest to the input x value
  // Performs 3-point Lagrange interpolation matching the spreadsheet behavior
  // Supports both pressure and temperature-based interpolation
  if (
    !propertyData ||
    !propertyData.dataGroupings ||
    propertyData.dataGroupings.length === 0
  ) {
    return 0;
  }

  // Decide the x-value based on controlType
  const controlValue =
    propertyData.type === VISCOSITY_TEMPERATURE_TYPE ? temperature : pressure;

  // Find the group with the closest average to control value
  let minDifference = Number.MAX_VALUE;
  let selectedGroup: LagrangeDataGrouping | null = null;

  for (const group of propertyData.dataGroupings) {
    const difference = Math.abs(controlValue - group.average);
    if (difference < minDifference) {
      minDifference = difference;
      selectedGroup = group;
    }
  }

  if (!selectedGroup) {
    return 0;
  }

  // Perform Lagrange interpolation with the selected group
  return lagrangeInterpolate(selectedGroup.points, controlValue);
}
